import { useState, useEffect, useCallback } from "react";
import {
  listSkills,
  createSkill,
  approveSkill,
  deleteSkill,
  getSkillDetail,
  promoteSkill,
  listAgents,
  bindAgentSkill,
  type Skill,
  type SkillTier,
  type Agent,
} from "../api/client.js";

const CATEGORIES = ["all", "security", "data", "code", "research", "automation", "custom"] as const;
const TIERS: Array<{ value: SkillTier | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "canonical", label: "Canonical" },
  { value: "community", label: "Community" },
  { value: "runtime", label: "Runtime" },
];

const RISK_COLORS: Record<string, string> = {
  low: "badge-green",
  medium: "badge-amber",
  high: "badge-red",
  critical: "badge-red",
};

const TIER_COLORS: Record<string, string> = {
  canonical: "bg-blue-50 text-blue-700",
  community: "bg-purple-50 text-purple-700",
  runtime: "bg-ink-100 text-ink-600",
};

export function SkillsPage() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<(Skill & { content?: string }) | null>(null);
  const [tierFilter, setTierFilter] = useState<SkillTier | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showBindModal, setShowBindModal] = useState<string | null>(null);

  const loadSkills = useCallback(async () => {
    try {
      const res = await listSkills({
        tier: tierFilter === "all" ? undefined : tierFilter,
        category: categoryFilter === "all" ? undefined : categoryFilter,
        search: search || undefined,
      });
      setSkills(res.skills);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  }, [tierFilter, categoryFilter, search]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  async function handleApprove(id: string) {
    try {
      await approveSkill(id);
      setSkills((prev) => prev.map((s) => (s.id === id ? { ...s, approved: true } : s)));
    } catch { /* */ }
  }

  async function handlePromote(id: string) {
    try {
      const promoted = await promoteSkill(id);
      setSkills((prev) => prev.map((s) => (s.id === id ? { ...s, ...promoted } : s)));
    } catch { /* */ }
  }

  async function handleDelete(id: string) {
    try {
      await deleteSkill(id);
      setSkills((prev) => prev.filter((s) => s.id !== id));
      if (selectedSkill?.id === id) setSelectedSkill(null);
    } catch { /* */ }
  }

  async function handleViewDetail(skill: Skill) {
    try {
      const detail = await getSkillDetail(skill.id);
      setSelectedSkill(detail);
    } catch {
      setSelectedSkill({ ...skill });
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
        <h1 className="text-sm font-semibold text-ink-900">Skills Catalog</h1>
        <div className="flex items-center gap-2">
          <span className="text-2xs text-ink-400">{skills.length} skill{skills.length !== 1 ? "s" : ""}</span>
          <button onClick={() => setShowUpload(true)} className="btn-primary text-xs">
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Upload Skill
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Filters sidebar */}
        <div className="flex w-52 shrink-0 flex-col border-r border-ink-100 bg-white">
          {/* Search */}
          <div className="border-b border-ink-100 px-4 py-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search skills..."
              className="input text-xs"
            />
          </div>

          {/* Tier filter */}
          <div className="border-b border-ink-100 px-4 py-3">
            <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-400">Tier</h3>
            <div className="space-y-1">
              {TIERS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTierFilter(t.value)}
                  className={`block w-full rounded-md px-2.5 py-1.5 text-left text-xs ${
                    tierFilter === t.value
                      ? "bg-ink-900 text-white font-medium"
                      : "text-ink-600 hover:bg-surface-1"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category filter */}
          <div className="px-4 py-3">
            <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-400">Category</h3>
            <div className="space-y-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`block w-full rounded-md px-2.5 py-1.5 text-left text-xs capitalize ${
                    categoryFilter === cat
                      ? "bg-ink-900 text-white font-medium"
                      : "text-ink-600 hover:bg-surface-1"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Skills grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-6 py-6">
            {loading ? (
              <LoadingSkeleton />
            ) : skills.length === 0 ? (
              <EmptyState
                hasFilters={tierFilter !== "all" || categoryFilter !== "all" || !!search}
                onClear={() => {
                  setTierFilter("all");
                  setCategoryFilter("all");
                  setSearch("");
                }}
                onUpload={() => setShowUpload(true)}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {skills.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    onView={() => handleViewDetail(skill)}
                    onApprove={() => handleApprove(skill.id)}
                    onPromote={() => handlePromote(skill.id)}
                    onDelete={() => handleDelete(skill.id)}
                    onBind={() => setShowBindModal(skill.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Skill detail panel */}
        {selectedSkill && (
          <SkillDetailPanel
            skill={selectedSkill}
            onClose={() => setSelectedSkill(null)}
            onBind={() => setShowBindModal(selectedSkill.id)}
          />
        )}
      </div>

      {showUpload && (
        <UploadSkillModal
          onClose={() => setShowUpload(false)}
          onUpload={async (content) => {
            const skill = await createSkill(content);
            setSkills((prev) => [skill, ...prev]);
            setShowUpload(false);
          }}
        />
      )}

      {showBindModal && (
        <BindToAgentModal
          skillId={showBindModal}
          onClose={() => setShowBindModal(null)}
          onBound={() => {
            setShowBindModal(null);
            setSkills((prev) =>
              prev.map((s) =>
                s.id === showBindModal ? { ...s, installCount: s.installCount + 1 } : s,
              ),
            );
          }}
        />
      )}
    </div>
  );
}

// ── Skill Card ──────────────────────────────────────────────────────────────

function SkillCard({
  skill,
  onView,
  onApprove,
  onPromote,
  onDelete,
  onBind,
}: {
  skill: Skill;
  onView: () => void;
  onApprove: () => void;
  onPromote: () => void;
  onDelete: () => void;
  onBind: () => void;
}) {
  return (
    <div
      className="card flex flex-col px-4 py-4 cursor-pointer hover:shadow-sm transition-shadow"
      onClick={onView}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-2">
          <SkillIcon className="h-4 w-4 text-ink-500" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-ink-900">{skill.name}</span>
          <div className="mt-0.5 flex items-center gap-2 text-2xs text-ink-400">
            <span className="font-mono">{skill.skillId}</span>
            <span>v{skill.version}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        <span className={`rounded-md px-1.5 py-0.5 text-2xs font-medium ${TIER_COLORS[skill.tier] ?? "bg-ink-100 text-ink-600"}`}>
          {skill.tier}
        </span>
        <span className={RISK_COLORS[skill.riskLevel] ?? "badge-gray"}>
          {skill.riskLevel}
        </span>
        {!skill.approved && <span className="badge-amber">Pending</span>}
        {skill.category && (
          <span className="rounded-md bg-surface-2 px-1.5 py-0.5 text-2xs text-ink-500 capitalize">
            {skill.category}
          </span>
        )}
      </div>

      {skill.tools.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1">
          {skill.tools.slice(0, 3).map((tool) => (
            <span key={tool} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-2xs text-ink-600">
              {tool}
            </span>
          ))}
          {skill.tools.length > 3 && (
            <span className="text-2xs text-ink-400">+{skill.tools.length - 3}</span>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between border-t border-ink-50 pt-2.5">
        <div className="flex items-center gap-2 text-2xs text-ink-400">
          {skill.author && (
            <span className="flex items-center gap-1">
              {skill.authorVerified && <VerifiedIcon className="h-3 w-3 text-blue-500" />}
              {skill.author}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {skill.approved && (
            <button onClick={onBind} className="btn-ghost text-2xs text-primary" title="Bind to agent">
              Bind
            </button>
          )}
          {!skill.approved && (
            <button onClick={onApprove} className="btn-ghost text-2xs text-emerald-600" title="Approve">
              Approve
            </button>
          )}
          {skill.tier === "community" && skill.approved && (
            <button onClick={onPromote} className="btn-ghost text-2xs text-blue-600" title="Promote">
              Promote
            </button>
          )}
          <button onClick={onDelete} className="btn-ghost text-2xs text-danger" title="Delete">
            <TrashIcon className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Skill Detail Panel ──────────────────────────────────────────────────────

function SkillDetailPanel({
  skill,
  onClose,
  onBind,
}: {
  skill: Skill & { content?: string };
  onClose: () => void;
  onBind: () => void;
}) {
  return (
    <div className="flex w-80 shrink-0 flex-col border-l border-ink-100 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-ink-900">{skill.name}</h3>
        <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-2">
          <MetaRow label="ID" value={skill.skillId} mono />
          <MetaRow label="Version" value={`v${skill.version}`} />
          <MetaRow label="Author" value={skill.author ?? "Unknown"} />
          <MetaRow label="Tier" value={skill.tier} />
          <MetaRow label="Risk Level" value={skill.riskLevel} />
          <MetaRow label="Category" value={skill.category ?? "None"} />
          <MetaRow label="Approved" value={skill.approved ? "Yes" : "No"} />
        </div>

        {skill.tools.length > 0 && (
          <div>
            <h4 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">
              Required Tools
            </h4>
            <div className="flex flex-wrap gap-1">
              {skill.tools.map((tool) => (
                <span key={tool} className="rounded bg-surface-2 px-2 py-0.5 font-mono text-2xs text-ink-600">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        )}

        {skill.tags && skill.tags.length > 0 && (
          <div>
            <h4 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">Tags</h4>
            <div className="flex flex-wrap gap-1">
              {skill.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-500">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {skill.content && (
          <div>
            <h4 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">Skill Body</h4>
            <pre className="max-h-64 overflow-auto rounded-lg bg-ink-950 px-3 py-2 font-mono text-xs text-emerald-300 whitespace-pre-wrap">
              {extractBody(skill.content)}
            </pre>
          </div>
        )}
      </div>

      {skill.approved && (
        <div className="border-t border-ink-100 px-4 py-3">
          <button onClick={onBind} className="btn-primary text-xs w-full">Bind to Agent</button>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-2xs text-ink-400">{label}</span>
      <span className={`text-2xs text-ink-700 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function extractBody(content: string): string {
  const match = content.match(/---[\s\S]*?---\s*([\s\S]*)/);
  return match ? match[1].trim() : content;
}

// ── Bind to Agent Modal ─────────────────────────────────────────────────────

function BindToAgentModal({
  skillId,
  onClose,
  onBound,
}: {
  skillId: string;
  onClose: () => void;
  onBound: () => void;
}) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [binding, setBinding] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listAgents()
      .then((res) => setAgents(res.agents ?? res))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleBind(agentId: string) {
    setBinding(agentId);
    setError("");
    try {
      await bindAgentSkill(agentId, skillId);
      onBound();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to bind");
      setBinding(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-sm animate-slide-up rounded-2xl bg-white shadow-elevated">
        <div className="border-b border-ink-100 px-6 pb-3 pt-5">
          <h2 className="text-base font-semibold text-ink-900">Bind to Agent</h2>
          <p className="mt-0.5 text-xs text-ink-500">Select an agent to bind this skill to</p>
        </div>

        <div className="max-h-60 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
            </div>
          ) : agents.length === 0 ? (
            <div className="py-8 text-center text-xs text-ink-400">No agents available</div>
          ) : (
            agents.map((agent) => (
              <div key={agent.id} className="flex items-center justify-between rounded-lg px-4 py-3 hover:bg-surface-1">
                <div>
                  <span className="text-sm font-medium text-ink-800">{agent.name}</span>
                  <div className="text-2xs text-ink-400">{agent.model}</div>
                </div>
                <button
                  onClick={() => handleBind(agent.id)}
                  disabled={binding === agent.id}
                  className="btn-primary text-xs px-3 py-1.5"
                >
                  {binding === agent.id ? "..." : "Bind"}
                </button>
              </div>
            ))
          )}
        </div>

        {error && <div className="px-6 pb-2 text-xs text-danger">{error}</div>}

        <div className="border-t border-ink-100 px-6 py-3">
          <button onClick={onClose} className="btn-secondary text-xs w-full">Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Upload Modal ────────────────────────────────────────────────────────────

function UploadSkillModal({
  onClose,
  onUpload,
}: {
  onClose: () => void;
  onUpload: (content: string) => Promise<void>;
}) {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await onUpload(content.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload skill");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-xl animate-slide-up rounded-2xl bg-white p-6 shadow-elevated">
        <h2 className="text-base font-semibold text-ink-900">Upload Skill</h2>
        <p className="mt-1 text-xs text-ink-500">
          Paste the SKILL.md content below (YAML frontmatter + markdown body)
        </p>

        <form onSubmit={handleSubmit} className="mt-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="input min-h-[16rem] resize-y font-mono text-xs"
            placeholder={`---\nid: my-skill\nname: My Skill\nversion: 1.0.0\nauthor: admin\ntools:\n  - web_search\nrisk_level: low\n---\n\nYou are a helpful skill that...`}
            required
          />

          {error && <p className="mt-2 text-xs text-danger">{error}</p>}

          <div className="mt-4 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-secondary text-xs">Cancel</button>
            <button type="submit" disabled={submitting || !content.trim()} className="btn-primary text-xs">
              {submitting ? "Uploading..." : "Upload Skill"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Empty/Loading States ────────────────────────────────────────────────────

function EmptyState({
  hasFilters,
  onClear,
  onUpload,
}: {
  hasFilters: boolean;
  onClear: () => void;
  onUpload: () => void;
}) {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2">
          <SkillIcon className="h-6 w-6 text-ink-400" />
        </div>
        {hasFilters ? (
          <>
            <h2 className="text-sm font-semibold text-ink-800">No matching skills</h2>
            <p className="mt-1 text-xs text-ink-500">Try adjusting your filters</p>
            <button onClick={onClear} className="btn-secondary mt-4 text-xs">Clear Filters</button>
          </>
        ) : (
          <>
            <h2 className="text-sm font-semibold text-ink-800">No skills loaded</h2>
            <p className="mt-1 text-xs text-ink-500">Upload a SKILL.md file to get started</p>
            <button onClick={onUpload} className="btn-primary mt-4 text-xs">Upload Skill</button>
          </>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="card animate-pulse px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-surface-2" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-24 rounded bg-surface-2" />
              <div className="h-2.5 w-32 rounded bg-surface-2" />
            </div>
          </div>
          <div className="mt-3 flex gap-1.5">
            <div className="h-4 w-14 rounded bg-surface-2" />
            <div className="h-4 w-10 rounded bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Icons ───────────────────────────────────────────────────────────────────

function SkillIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
      <path d="M5 5h6M5 8h4" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 4h12M5.5 4V2.5h5V4M6 7v5M10 7v5M3.5 4l.5 9.5a1 1 0 0 0 1 .5h6a1 1 0 0 0 1-.5L12.5 4" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function VerifiedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0l2.2 2.5L13 2l.5 2.8L16 6.5 14.5 9l1.5 2.5-2.8.5L13 14l-2.8.5L8 16l-2.2-2.5L3 14l-.5-2.8L0 9.5 1.5 7 0 4.5l2.8-.5L3 2l2.8-.5L8 0zm-1 11.5l5-5-1.5-1.5L7 8.5 5.5 7 4 8.5l3 3z" />
    </svg>
  );
}
